import React, { useState } from 'react';
import styles from './styles.module.css';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import UserTable from './UserTable';
import { Button, Input, Dropdown } from '../../../../common/ui';
import Axios from '../../../../common/util/Axios';
import store from '../../../../store';
import { setBranchId } from '../../../auth/actions';
import { useNavigate } from 'react-router-dom';

const Users = ({
  usersState,
  auth: { branchList, branchId, companyId },
  setBranchId,
}) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const columns = [
    { id: 'name', label: 'Name' },
    { id: 'email', label: 'Email' },
    { id: 'phone', label: 'Phone' },
    { id: 'role', label: 'Role' },
    { id: 'branch', label: 'Branch' },
    { id: 'status', label: 'Status' },
  ];

  const formatCsvValue = (value) => {
    if (value === null || value === undefined) return '""';
    const stringValue = String(value).replace(/"/g, '""');
    return `"${stringValue}"`;
  };

  const getTimestampString = () => {
    const pad = (num) => String(num).padStart(2, '0');
    const d = new Date();
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
      d.getDate()
    )}_${pad(d.getHours())}-${pad(d.getMinutes())}-${pad(d.getSeconds())}`;
  };

  const exportUsersToCsv = (rows, cols, filename) => {
    const header = cols.map((c) => formatCsvValue(c.label)).join(',');
    const body = (rows || [])
      .map((row) => cols.map((c) => formatCsvValue(row[c.id])).join(','))
      .join('\n');
    const csvContent = `\ufeff${header}${body ? `\n${body}` : ''}`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleChangeBranch = (value) => {
    setBranchId(value);
  };

  const onClickNewUser = () => {
    navigate('/iam/users/new-user');
  };

  const handleExport = async () => {
    try {
      const requestBody = {
        filename: 'users.xlsx',
        sheetName: 'Users',
        columns: [
          { key: 'name', header: 'Name' },
          { key: 'email', header: 'Email' },
          { key: 'phone', header: 'Phone' },
          { key: 'role', header: 'Role' },
          { key: 'branch', header: 'Branch' },
          { key: 'status', header: 'Status' },
        ],
        companyId: store.getState().authState.companyId,
      };

      const response = await Axios.post(
        '/api/dashboard/users/export-users-excel',
        requestBody,
        {
          responseType: 'blob',
          headers: {
            Accept:
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          },
        }
      );

      const contentDisposition = response?.headers?.['content-disposition'];
      let downloadFileName =
        requestBody.filename || `export_${getTimestampString()}.xlsx`;
      if (contentDisposition && contentDisposition.includes('filename=')) {
        const match = contentDisposition.match(
          /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i
        );
        downloadFileName = decodeURIComponent(
          match?.[1] || match?.[2] || downloadFileName
        );
      }

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', downloadFileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      try {
        const rows = usersState?.usersData || [];
        const filename = `users_export_${getTimestampString()}.csv`;
        exportUsersToCsv(rows, columns, filename);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Export failed', error);
      }
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <h1 className={styles.pageTitle}>Users</h1>
          <p className={styles.breadcrumb}>IAM / Users</p>
        </div>
        {branchList && branchList.length > 0 && branchId && (
          <div className={styles.branchSelectWrap}>
            <Dropdown
              label="Branch"
              id="branch-select"
              options={branchList.map((item) => ({
                value: item.id,
                label: item.name,
              }))}
              value={branchId}
              onChange={handleChangeBranch}
              aria-label="Select branch"
            />
          </div>
        )}
      </div>
      <div className={styles.body}>
        <div className={styles.chartMainDiv}>
          <div className={styles.topDiv}>
            <div className={styles.leftDiv}>
              <Button onClick={onClickNewUser}>New User</Button>
            </div>
            <div className={styles.rightDiv}>
              <Input
                id="search-input"
                placeholder="Search users..."
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                className={styles.searchInput}
              />
              <Button variant="secondary" onClick={handleExport}>
                Export
              </Button>
            </div>
          </div>
          <div className={styles.chartDiv}>
            <UserTable searchKeyword={searchTerm} />
          </div>
        </div>
      </div>
    </div>
  );
};

Users.propTypes = {
  usersState: PropTypes.shape({
    usersData: PropTypes.array,
  }),
  auth: PropTypes.shape({
    branchList: PropTypes.array,
    branchId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  setBranchId: PropTypes.func.isRequired,
};

Users.defaultProps = {
  usersState: {},
  auth: {},
};

const mapStateToProps = (state) => ({
  usersState: state.usersState,
  auth: state.authState,
});

export default connect(mapStateToProps, { setBranchId })(Users);
